import React from 'react'
import ReactDOM from 'react-dom/client'
import { Provider } from 'react-redux'
import { unstable_HistoryRouter as Router } from 'react-router-dom'
import { ErrorBoundary } from 'react-error-boundary'

import 'antd/dist/antd.less'
import './assets/global.scss'

import App from './App'
import { store } from './store'
import history from 'src/libs/history'
import { ErrorFallback } from 'src/app/exception/ErrorBoundary'

// [SCOPE] Global handler for unhandled promise rejections — fires for any async throw not caught elsewhere.
// Lazy import breaks the circular dependency: tracker → TrackerRequest → HTTPRequest (api/index.tsx).
// Throttle + dedup guard added: a tight rejection loop (e.g. a retry storm) must not spam
// the tracking endpoint indefinitely. Caps to MAX_TRACKED_REJECTIONS_PER_WINDOW per rolling
// window and skips an identical message/url repeated within DEDUP_WINDOW_MS.
const MAX_TRACKED_REJECTIONS_PER_WINDOW = 20
const RATE_WINDOW_MS = 60_000
const DEDUP_WINDOW_MS = 5_000
let rateWindowStart = Date.now()
let rateWindowCount = 0
const lastSeenAt = new Map<string, number>()

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason
  const now = Date.now()

  if (now - rateWindowStart > RATE_WINDOW_MS) {
    rateWindowStart = now
    rateWindowCount = 0
  }
  if (rateWindowCount >= MAX_TRACKED_REJECTIONS_PER_WINDOW) {
    return
  }

  const message =
    reason instanceof Error ? reason.message : String(reason ?? 'Unknown')
  const dedupKey = `${message}|${window.location.pathname}`
  const lastSentAt = lastSeenAt.get(dedupKey)
  if (lastSentAt !== undefined && now - lastSentAt < DEDUP_WINDOW_MS) {
    return
  }
  lastSeenAt.set(dedupKey, now)
  rateWindowCount += 1

  import('src/utils/tracker').then(({ trackError }) => {
    trackError({
      error_type: 'unhandled_rejection',
      message,
      stack_trace: reason instanceof Error ? reason.stack : undefined,
      url: window.location.pathname,
    })
  })
})

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)
root.render(
  <Provider store={store}>
    <Router history={history}>
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onReset={() => {
          // reset the state of your app so the error doesn't happen again
        }}
      >
        <App />
      </ErrorBoundary>
    </Router>
  </Provider>,
)
